import { useEffect, useState } from 'react';
import UploadModal from './UploadModal';
import { useAuth } from '../context/AuthContext';
import { exportRowsToCsv } from '../utils/downloadTemplate';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const emptyFormFromFields = (fields) => Object.fromEntries(fields.map((f) => [f.name, '']));

// api.list is a paged, server-side-searched call: ({ page, limit, search }) => { items, total }.
// The master registers can hold thousands of rows, so search has to run in the database —
// filtering a client-side array would only ever see whatever page happened to be fetched,
// and silently miss every match past it.
export default function MasterTablePage({ title, columns, fields, api, uploadTemplateHint }) {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyFormFromFields(fields));
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search box so each keystroke doesn't fire its own request.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // A new search term always restarts at page 1 — otherwise "page 4" of the old
  // unfiltered list could be past the end of a narrower result set.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  async function refresh() {
    setLoading(true);
    const { items: pageItems, total: matchTotal } = await api.list({
      page,
      limit: PAGE_SIZE,
      search: debouncedSearch,
    });
    setItems(pageItems);
    setTotal(matchTotal);
    setLoading(false);
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]);

  function openAddForm() {
    setEditingId(null);
    setForm(emptyFormFromFields(fields));
    setFormError('');
    setShowForm(true);
  }

  function openEditForm(item) {
    setEditingId(item._id);
    setForm(Object.fromEntries(fields.map((f) => [f.name, item[f.name] ?? ''])));
    setFormError('');
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      if (editingId) {
        await api.update(editingId, form);
      } else {
        await api.create(form);
      }
      closeForm();
      refresh();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this record?')) return;
    await api.remove(id);
    refresh();
  }

  // Exports only the currently loaded page, matching what's on screen — not the
  // full (possibly much larger) result set. Narrow the search first for a full export.
  function handleExport() {
    const filename = `${title.toLowerCase().replace(/\s+/g, '-')}.csv`;
    exportRowsToCsv(filename, columns, items);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold text-gray-800">{title}</h1>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            disabled={items.length === 0}
            title="Exports the currently loaded page only — narrow your search first to export a specific set"
            className="px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Export CSV (this page)
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => setShowUpload(true)}
                className="px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50"
              >
                Bulk Upload
              </button>
              <button
                onClick={() => (showForm ? closeForm() : openAddForm())}
                className="px-3 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white"
              >
                + Add
              </button>
            </>
          )}
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          {formError && (
            <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {formError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {fields.map((f) => (
              <div key={f.name}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                <input
                  required={f.required !== false}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeForm}
              className="px-3 py-1.5 text-sm rounded border border-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
            >
              {saving ? 'Saving…' : editingId ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${columns.map((c) => c.label).join(', ').toLowerCase()}…`}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-72"
          />
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {total === 0 ? '0 records' : `${rangeStart}–${rangeEnd} of ${total}`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="px-4 py-2 font-medium">
                  {c.label}
                </th>
              ))}
              {isAdmin && <th className="px-4 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-6 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-6 text-center text-gray-400">
                  {debouncedSearch ? 'No records match your search' : 'No records yet'}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item._id} className="border-t border-gray-100">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-2 text-gray-700">
                      {String(item[c.key] ?? '')}
                    </td>
                  ))}
                  {isAdmin && (
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEditForm(item)}
                        className="text-blue-600 hover:text-blue-800 text-xs mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-xs text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showUpload && (
        <UploadModal
          title={`Bulk Upload ${title}`}
          templateHint={uploadTemplateHint}
          fields={fields}
          onUpload={api.bulkUpload}
          onClose={() => {
            setShowUpload(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}
